import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Svg from 'react-native-svg';
import { Avatar, Box, HStack, Pressable, Text, VStack } from 'native-base';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../../../../utils/fonts';
import { COLORS } from '../../../../utils/colors';
import {
  addIconSVG,
  deleteCardsIconSVG,
  speakerImageBGSVG,
  speakerNoPersonSVG,
} from '../../../../utils/svg';
import CommonAvatar from '../../../../components/avatar';
import NewTextAreaField from '../../../../components/textArea/newTextArea';
import InputField from '../../../../components/input';
import TimeInputField from '../../../../components/timeInput';
import { EventPlanDetailsCards } from './eventPlanDetailsCard';
import { EventPlanTextCards } from './eventPlanTextAreaCard';
import config from '../../../../utils/config';
import { uploadFileAPI } from '../../../../api/apis/workflow';
import { Toaster } from '../../../../components/toaster';
import { LoaderAnimation } from '../../../../components/spinner';
import BulkUploadMenuModal from './bulkUploadMenuModal';

const CreateEventBreakDown = forwardRef(
  (
    {
      eventTab,
      defaultValue,
      type,
      forceRemount,
      childref,
      agendaRef,
      eventId,
      setForceRemount = () => { },
      setEventBreakError = () => { },
      getEventDetails = () => { },
      currentTab = 'Day 1',
      updateSelectedDay = () => { },
      dateOfEvent,
    },
    ref
  ) => {
    const [defaultData, setDefaultData] = useState(defaultValue);
    const [sessions, setSessions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [uploadingFiles, setUploadingFiles] = useState({});
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [timeValidationErrors, setTimeValidationErrors] = useState({});
    const [speakerValidationErrors, setSpeakerValidationErrors] = useState({});

    let fileInputRef = useRef();
    const toaster = Toaster();


    useEffect(() => {
      setDefaultData(defaultValue);

      // Check if there's existing agenda data to prefill sessions
      if (defaultValue && defaultValue?.agenda && defaultValue?.agenda?.length > 0) {
        
        // Process agenda data immediately
        const processAgendaData = () => {
          const existingSessions = defaultValue?.agenda?.map((agendaItem, index) => {
          const session = {
            id: Date.now() + index,
            type: agendaItem.type || '',
            start_time: agendaItem.start_time || '',
            end_time: agendaItem.end_time || '',
            session_id: agendaItem.session_id || '',
            speakers: agendaItem.speakers || []
          };
          
          // Add type-specific fields based on the agenda item type
          const agendaType = agendaItem.type ? agendaItem.type.toLowerCase() : '';
          
          switch (agendaType) {
            case 'speaker':
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || '',
                speakers: agendaItem?.speakers || []
              };

            case 'break':
            case 'break 1': // Handle different break types
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || ''
              };

            case 'sightseeing':
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || ''
              };

            default:
              // For empty or custom types, include basic fields and speakers
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || '',
                speakers: agendaItem?.speakers || []
              };
          }
        });
        
        // Keep speakers as they are - don't add default speakers
        const sessionsWithSpeakers = existingSessions.map(session => ({
          ...session,
          speakers: session.speakers || [] // Keep existing speakers or empty array
        }));
        
          setSessions(sessionsWithSpeakers);
        };
        
        // Process data immediately
        processAgendaData();
      } else {
        // Initialize with one session only, no speakers by default
        setSessions([
          {
            id: Date.now(),
            type: '',
            title: '',
            location: '',
            start_time: '',
            end_time: '',
            description: '',
            speakers: [] // Empty speakers array - user needs to click add to add speakers
          }
        ]);
      }
    }, [defaultValue, eventTab]);

    // Additional useEffect to ensure sessions are set when data is available
    useEffect(() => {      
      // If sessions is empty but we have defaultValue with agenda data, process it
      if ((!sessions || sessions.length === 0) && defaultValue && defaultValue?.agenda && defaultValue?.agenda?.length > 0) {
        const existingSessions = defaultValue?.agenda?.map((agendaItem, index) => {
          const session = {
            id: Date.now() + index,
            type: agendaItem.type || '',
            start_time: agendaItem.start_time || '',
            end_time: agendaItem.end_time || '',
            session_id: agendaItem.session_id || '',
            speakers: agendaItem.speakers || []
          };
          
          const agendaType = agendaItem.type ? agendaItem.type.toLowerCase() : '';
          
          switch (agendaType) {
            case 'speaker':
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || '',
                speakers: agendaItem?.speakers || []
              };
            case 'break':
            case 'break 1':
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || ''
              };
            default:
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || '',
                speakers: agendaItem?.speakers || []
              };
          }
        });
        
        const sessionsWithSpeakers = existingSessions.map(session => ({
          ...session,
          speakers: session.speakers || [] // Keep existing speakers or empty array
        }));
        
        setSessions(sessionsWithSpeakers);
      }
    }, [sessions, defaultValue]);

    // Force update sessions when modal opens for editing
    useEffect(() => {
      if (type === 'edit' && defaultValue && defaultValue?.agenda && defaultValue?.agenda?.length > 0) {
        
        const existingSessions = defaultValue?.agenda?.map((agendaItem, index) => {
          const session = {
            id: Date.now() + index,
            type: agendaItem.type || '',
            start_time: agendaItem.start_time || '',  
            end_time: agendaItem.end_time || '',
            session_id: agendaItem.session_id || '',
            speakers: agendaItem.speakers || []
          };
          
          const agendaType = agendaItem.type ? agendaItem.type.toLowerCase() : '';
          
          switch (agendaType) {
            case 'speaker':
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || '',
                speakers: agendaItem?.speakers || []
              };
            case 'break':
            case 'break 1':
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || ''
              };
            default:
              return {
                ...session,
                title: agendaItem?.title || '',
                location: agendaItem?.location || '',
                description: agendaItem?.description || '',
                speakers: agendaItem?.speakers || []
              };
          }
        });
        
        const sessionsWithSpeakers = existingSessions.map(session => ({
          ...session,
          speakers: session.speakers || [] // Keep existing speakers or empty array
        }));
        
        setSessions(sessionsWithSpeakers);
        
        // Additional timeout-based fallback to ensure data is set
        setTimeout(() => {
          if (!sessions || sessions.length === 0) {
            setSessions(sessionsWithSpeakers);
          }
        }, 100);
      }
    }, [type, defaultValue]);

    // Removed aggressive forceRemount that was causing focus loss
    // The form fields should update naturally when sessions change

    // Cleanup function to remove file input element when component unmounts
    useEffect(() => {
      return () => {
        if (fileInputRef.current && fileInputRef.current.parentNode) {
          fileInputRef.current.parentNode.removeChild(fileInputRef.current);
        }
      };
    }, []);

    // Helper function to validate image dimensions
    const validateImageDimensions = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          // Recommended dimensions: 250x360px (as mentioned in the UI)
          const recommendedWidth = 250;
          const recommendedHeight = 360;

          if (img.width < recommendedWidth || img.height < recommendedHeight) {
            resolve({
              valid: false,
              message: `Image dimensions should be at least ${recommendedWidth}x${recommendedHeight}px. Current: ${img.width}x${img.height}px`
            });
          } else {
            resolve({ valid: true });
          }
        };
        img.onerror = () => {
          resolve({ valid: true }); // Skip validation if image can't be loaded
        };
        img.src = URL.createObjectURL(file);
      });
    };

    const handleInputChange = async (e, sessionIndex, speakerIndex) => {
      const file = e.target.files[0];

      if (!file) return;

      let fileExtension = file.name.split('.').pop().toLowerCase();

      // Check file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toaster('File size should be less than 5MB', 'error', 'error');
        e.target.value = null;
        return;
      }

      if (
        fileExtension === 'jpg' ||
        fileExtension === 'jpeg' ||
        fileExtension === 'png'
      ) {
        // Set loading state for this specific file upload
        const uploadKey = `${sessionIndex}_${speakerIndex}`;
        
        try {
          setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

          // Validate image dimensions
          const dimensionValidation = await validateImageDimensions(file);
          if (!dimensionValidation.valid) {
            toaster(dimensionValidation.message, 'warning', 'warning');
            // Continue with upload but show warning
          }

          // First, update the speaker with the file object for immediate display
          const updatedSessions = sessions.map((session, sIndex) => {
            if (sIndex === sessionIndex) {
              return {
                ...session,
                speakers: session.speakers.map((speaker, spIndex) => {
                  if (spIndex === speakerIndex) {
                    return {
                      ...speaker,
                      file_name: file
                    };
                  }
                  return speaker;
                })
              };
            }
            return session;
          });
          setSessions(updatedSessions);

          // Upload the file to server
          const uploadResponse = await uploadFileAPI([file], 'image', 'image');

          if (uploadResponse && uploadResponse.length > 0 && !uploadResponse[0].error) {
            // Update the speaker with the uploaded file name
            const uploadedFileName = uploadResponse[0].data.data.data.file_name;
            setSessions(prevSessions => prevSessions.map((session, sIndex) => {
              if (sIndex === sessionIndex) {
                return {
                  ...session,
                  speakers: session.speakers.map((speaker, spIndex) => {
                    if (spIndex === speakerIndex) {
                      return {
                        ...speaker,
                        file_name: uploadedFileName
                      };
                    }
                    return speaker;
                  })
                };
              }
              return session;
            }));
            toaster('Image uploaded successfully', 'success', 'success');
          } else {
            toaster('Failed to upload image', 'error', 'error');
            // Remove the file if upload failed
            setSessions(prevSessions => prevSessions.map((session, sIndex) => {
              if (sIndex === sessionIndex) {
                return {
                  ...session,
                  speakers: session.speakers.map((speaker, spIndex) => {
                    if (spIndex === speakerIndex) {
                      return {
                        ...speaker,
                        file_name: ''
                      };
                    }
                    return speaker;
                  })
                };
              }
              return session;
            }));
          }
        } catch (error) {
          console.error('Error uploading file:', error);
          toaster('Failed to upload image', 'error', 'error');
          // Remove the file if upload failed
          setSessions(prevSessions => prevSessions.map((session, sIndex) => {
            if (sIndex === sessionIndex) {
              return {
                ...session,
                speakers: session.speakers.map((speaker, spIndex) => {
                  if (spIndex === speakerIndex) {
                    return {
                      ...speaker,
                      file_name: ''
                    };
                  }
                  return speaker;
                })
              };
            }
            return session;
          }));
        } finally {
          // Clear loading state
          setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
        }

        e.target.value = null;
      } else {
        toaster('Please select a valid image file (JPG, JPEG, PNG)', 'error', 'error');
        e.target.value = null;
      }
    };

    const handleFileSelect = (sessionIndex, speakerIndex) => {
      setSelectedIndex(sessionIndex);

      // Store current indices in the ref for use in the handler
      if (fileInputRef.current) {
        fileInputRef.current.indices = { sessionIndex, speakerIndex };
        // Trigger the file input click
        fileInputRef.current.click();
      } else {
        // If ref is not ready, create the input element
        const input = document.createElement('input');
        input.type = 'file';
        input.style.display = 'none';
        input.accept = '.png,.jpg,.jpeg';
        input.onchange = (e) => {
          const { sessionIndex: sIndex, speakerIndex: spIndex } = fileInputRef.current?.indices || {};
          handleInputChange(e, sIndex, spIndex);
        };
        document.body.appendChild(input);
        fileInputRef.current = input;
        fileInputRef.current.indices = { sessionIndex, speakerIndex };
        fileInputRef.current.click();
      }
    };

    const clearValidationError = (fieldName, index) => {
      if (timeValidationErrors[`${fieldName}_${index}`]) {
        setTimeValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`${fieldName}_${index}`];
          return newErrors;
        });
      }
    };

    const onSessionInputChange = (e, fieldName, ind) => {
        // Clear validation error for all fields when user starts typing
        if (['start_time', 'end_time', 'type', 'title', 'location', 'speaker_name', 'description'].includes(fieldName)) {
          clearValidationError(fieldName, ind);
        }

      const updatedSessions = sessions.map((item, index) => {
        if (index === ind) {
          return {
            ...item,
            [fieldName]: e.target.value
          };
        }
        return item;
      });
      setSessions(updatedSessions);
    };

    const onChangeSessionType = (e, ind) => {
      const value = e.target.value;
      const updatedSessions = sessions.map((item, index) => {
        if (index === ind) {
          // Only update the type field, preserve all other existing field values
          return {
            ...item,
            type: value
          };
        }
        return item;
      });
      setSessions(updatedSessions);
    };

    //     const onSubmitInput = () => {
    //       // Convert sessions back to the original format
    //       const speakerSessions = sessions.filter(s => s.type === 'speaker');
    //       const breakSessions = sessions.filter(s => s.type === 'break');
    //       const sightseeingSessions = sessions.filter(s => s.type === 'sightseeing');
    //       return {
    //         ...defaultData,
    //         speaker: speakerSessions,
    //         break: breakSessions,
    //         sightseeing: sightseeingSessions,
    //       };
    //     };

    const onSubmitInput = () => {
      // Validate start_time and end_time for all sessions
      const errors = {};
      let hasErrors = false;

      sessions.forEach((session, index) => {
        if (!session.start_time) {
          errors[`start_time_${index}`] = 'Please select Start Time';
          hasErrors = true;
        }
        if (!session.end_time) {
          errors[`end_time_${index}`] = 'Please select End Time';
          hasErrors = true;
        }

        // Validate mandatory fields
        if (!session.type || session.type.trim() === '') {
          errors[`type_${index}`] = 'Please enter Session Type';
          hasErrors = true;
        }
        if (!session.title) {
          errors[`title_${index}`] = 'Please enter Title';
          hasErrors = true;
        }
        // if (!session.location) {
        //   errors[`location_${index}`] = 'Please enter Location';
        //   hasErrors = true;
        // }
        // Location and Description are optional, no validation needed

        // Validate speakers for this session
        if (session.speakers && session.speakers.length > 0) {
          session.speakers.forEach((speaker, speakerIndex) => {
            if (!speaker.speaker_name) {
              errors[`speaker_name_${index}_${speakerIndex}`] = 'Please enter Speaker Name';
              hasErrors = true;
            }
            if (!speaker.speaker_designation) {
              errors[`speaker_designation_${index}_${speakerIndex}`] = 'Please enter Speaker Designation';
              hasErrors = true;
            }
          });
        }
      });

      if (hasErrors) {
        setTimeValidationErrors(errors);
        setSpeakerValidationErrors(errors);
        return false;
      }

      setTimeValidationErrors({});

      // Convert sessions to new payload format
      const sessionsPayload = sessions.map((session) => {
        const sessionPayload = {
          start_time: session?.start_time || '',
          end_time: session?.end_time || '',
          type: session?.type || '',
          speaker_info: session?.speakers?.map(speaker => ({
            speaker_name: speaker?.speaker_name || '',
            speaker_designation: speaker?.speaker_designation || '',
            speaker_topic: speaker?.speaker_topic || '',
            speaker_details: speaker?.speaker_details || '',
            file_name: speaker?.file_name || '',
            link: '' // Empty as requested
          })) || [],
          title: session?.title || '',
          location: session?.location || '',
          description: session?.description || '',
          dress_code: '', // Empty as requested
          attendee_persona: [] // Empty as requested
        };

        // Only include session_id for edit operations (when session_id exists)
        if (session?.session_id && session.session_id.trim() !== '') {
          sessionPayload.session_id = session.session_id;
        }

        return sessionPayload;
      });

      // Return the new payload structure (date will be calculated by parent component)
      return {
        sessions: sessionsPayload
      };
    };

    useImperativeHandle(ref, () => ({
      onSubmitInput
    }));

    const uploadPhoto = (sessionIndex, speakerIndex) => {
      handleFileSelect(sessionIndex, speakerIndex);
    };

    const handleAddSession = () => {
      const newSession = {
        id: Date.now(),
        type: '', // Empty type for free input
        title: '',
        location: '',
        start_time: '',
        end_time: '',
        description: '',
        speakers: [] // Empty speakers array - user needs to click add to add speakers
      };
      setSessions([...sessions, newSession]);
    };

    const handleDeleteSession = (ind) => {
      const updatedSessions = sessions.filter((_, index) => index !== ind);
      setSessions(updatedSessions);
      setForceRemount(!forceRemount);
    };

    const handleAddSpeaker = (sessionIndex) => {
      const newSpeaker = {
        id: Date.now(),
        speaker_name: '',
        speaker_designation: '',
        speaker_topic: '',
        speaker_details: '',
        file_name: ''
      };
      
      const updatedSessions = sessions.map((session, index) => {
        if (index === sessionIndex) {
          // If no speakers exist, add one default speaker
          if (!session.speakers || session.speakers.length === 0) {
            return {
              ...session,
              speakers: [newSpeaker]
            };
          } else {
            // Add additional speaker
            return {
              ...session,
              speakers: [...session.speakers, newSpeaker]
            };
          }
        }
        return session;
      });
      setSessions(updatedSessions);
    };

    const handleDeleteSpeaker = (sessionIndex, speakerIndex) => {
      const updatedSessions = sessions.map((session, index) => {
        if (index === sessionIndex) {
          return {
            ...session,
            speakers: session.speakers.filter((_, spIndex) => spIndex !== speakerIndex)
          };
        }
        return session;
      });
      setSessions(updatedSessions);
      setForceRemount(!forceRemount);
    };

    const onSpeakerInputChange = (e, fieldName, sessionIndex, speakerIndex) => {
      // Clear validation error when user starts typing
      if (['speaker_name', 'speaker_designation', 'speaker_topic', 'speaker_details'].includes(fieldName)) {
        const errorKey = `${fieldName}_${sessionIndex}_${speakerIndex}`;
        if (speakerValidationErrors[errorKey]) {
          setSpeakerValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            return newErrors;
          });
        }
      }

      const updatedSessions = sessions.map((session, sIndex) => {
        if (sIndex === sessionIndex) {
          return {
            ...session,
            speakers: session.speakers.map((speaker, spIndex) => {
              if (spIndex === speakerIndex) {
                return {
                  ...speaker,
                  [fieldName]: e.target.value
                };
              }
              return speaker;
            })
          };
        }
        return session;
      });
      setSessions(updatedSessions);
    };

    const getSpeakerBody = (speaker, sessionIndex, speakerIndex) => {
      return (
        <>
          <input
            type='file'
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept='.png,.jpg,.jpeg'
            onChange={(e) => {
              const { sessionIndex: sIndex, speakerIndex: spIndex } = fileInputRef.current?.indices || {};
              handleInputChange(e, sIndex, spIndex);
            }}
          />
          <HStack
            width={'100%'}
            pr={'24px'}
            py={'21px'}
          >
            <Box
              width={'20%'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <div style={{ position: 'relative' }}>
                {(!speaker?.file_name || speaker?.file_name === '') && !uploadingFiles[`${sessionIndex}_${speakerIndex}`] && (
                  <div style={{ position: 'absolute', bottom: 20, right: 21 }}>
                    <CommonAvatar
                      width={'24px'}
                      height={'24px'}
                      bgcolor={COLORS.TABS_ACTIVE_BORDER_COLOR}
                      svg={addIconSVG}
                      svgwh='24'
                      view='0 0 24 24'
                      onPress={() => {
                        setSelectedIndex(sessionIndex);
                        uploadPhoto(sessionIndex, speakerIndex);
                      }}
                      touchable={true}
                    />
                  </div>
                )}
                {uploadingFiles[`${sessionIndex}_${speakerIndex}`] && (
                  <div style={{ position: 'absolute', bottom: 20, right: 21 }}>
                    <Box
                      width={'24px'}
                      height={'24px'}
                      bgcolor={COLORS.TABS_ACTIVE_BORDER_COLOR}
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box
                        width="12px"
                        height="12px"
                        border="2px solid white"
                        borderTop="2px solid transparent"
                        borderRadius="full"
                        animation="spin 1s linear infinite"
                        style={{
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }}
                      />
                    </Box>
                  </div>
                )}
                {speaker?.file_name ? (
                  <div style={{ position: 'absolute', right: 10 }}>
                    <Avatar
                      size='2xl'
                      source={{
                        uri:
                          speaker.file_name instanceof File
                            ? URL.createObjectURL(speaker.file_name)
                            : config.bucketPath + speaker.file_name
                      }}
                      fallbackElement={
                        <Box
                          bg="gray.200"
                          size="2xl"
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {speakerNoPersonSVG}
                        </Box>
                      }
                    >
                      {uploadingFiles[`${sessionIndex}_${speakerIndex}`] && (
                        <Box
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          bg="rgba(0,0,0,0.7)"
                          borderRadius="full"
                          p={2}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box
                            width="16px"
                            height="16px"
                            border="2px solid white"
                            borderTop="2px solid transparent"
                            borderRadius="full"
                            animation="spin 1s linear infinite"
                            style={{
                              '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' }
                              }
                            }}
                          />
                        </Box>
                      )}
                    </Avatar>
                    <Svg
                      height='20'
                      width='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        cursor: uploadingFiles[`${sessionIndex}_${speakerIndex}`] ? 'not-allowed' : 'pointer',
                        opacity: uploadingFiles[`${sessionIndex}_${speakerIndex}`] ? 0.5 : 1
                      }}
                      onClick={() => {
                        if (uploadingFiles[`${sessionIndex}_${speakerIndex}`]) return; // Prevent deletion while uploading
                        const updatedSessions = sessions.map((session, sIndex) => {
                          if (sIndex === sessionIndex) {
                            return {
                              ...session,
                              speakers: session.speakers.map((sp, spIndex) => {
                                if (spIndex === speakerIndex) {
                                  return {
                                    ...sp,
                                    file_name: ''
                                  };
                                }
                                return sp;
                              })
                            };
                          }
                          return session;
                        });
                        setSessions(updatedSessions);
                      }}
                    >
                      <line
                        x1='18'
                        y1='6'
                        x2='6'
                        y2='18'
                      ></line>
                      <line
                        x1='6'
                        y1='6'
                        x2='18'
                        y2='18'
                      ></line>
                    </Svg>
                  </div>
                ) : (
                  <div style={{ position: 'absolute', top: 30, right: 45 }}>
                    {speakerNoPersonSVG}
                  </div>
                )}
                {speakerImageBGSVG}
              </div>
              <Text
                fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              >
                Upload speaker photo
              </Text>
              <Text
                fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                color={COLORS.LABEL_TEXT_COLOR}
              >
                Size 250X360px (JPG, PNG)
              </Text>
              <Text
                fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                color={COLORS.LABEL_TEXT_COLOR}
              >
                Max 5MB
              </Text>
            </Box>
            <VStack
              width={'80%'}
              space={'15px'}
            >
              <HStack
                width={'100%'}
                space={'24.5px'}
              >
                <InputField
                  name='speaker_name'
                  value={speaker.speaker_name}
                  label='Speaker Name'
                  placeholder='Enter Speaker Name'
                  width='50%'
                  onChange={(e) => onSpeakerInputChange(e, 'speaker_name', sessionIndex, speakerIndex)}
                  transparent={true}
                  ind={speakerIndex}
                  speaker
                  setEventBreakError={setEventBreakError}
                  important
                  isInvalid={!!speakerValidationErrors[`speaker_name_${sessionIndex}_${speakerIndex}`]}
                  errorMessage={speakerValidationErrors[`speaker_name_${sessionIndex}_${speakerIndex}`] || ''}
                />
                <InputField
                  name='speaker_designation'
                  value={speaker.speaker_designation}
                  label='Speaker Designation'
                  placeholder='Enter Speaker Designation'
                  width='50%'
                  onChange={(e) =>
                    onSpeakerInputChange(e, 'speaker_designation', sessionIndex, speakerIndex)
                  }
                  transparent={true}
                  ind={speakerIndex}
                  speaker
                  setEventBreakError={setEventBreakError}
                  important
                  isInvalid={!!speakerValidationErrors[`speaker_designation_${sessionIndex}_${speakerIndex}`]}
                  errorMessage={speakerValidationErrors[`speaker_designation_${sessionIndex}_${speakerIndex}`] || ''}
                />
              </HStack>
              <HStack
                width={'100%'}
                space={'24.5px'}
              >
                <Box width={'48.5%'}>
                  <NewTextAreaField
                    name='speaker_details'
                    value={speaker.speaker_details}
                    label='About Speaker'
                    onChange={(e) =>
                      onSpeakerInputChange(e, 'speaker_details', sessionIndex, speakerIndex)
                    }
                    placeholder='Enter About Speaker'
                    transparent={true}
                    ind={speakerIndex}
                    speaker
                    setEventBreakError={setEventBreakError}
                  />
                </Box>
                <Box width={'48.5%'}>
                  <NewTextAreaField
                    name='speaker_topic'
                    value={speaker.speaker_topic}
                    label='Speaker Topic'
                    onChange={(e) =>
                      onSpeakerInputChange(e, 'speaker_topic', sessionIndex, speakerIndex)
                    }
                    placeholder='Enter Speaker Topic'
                    transparent={true}
                    ind={speakerIndex}
                    speaker
                    setEventBreakError={setEventBreakError}
                  />
                </Box>
              </HStack>
            </VStack>
          </HStack>
        </>
      );
    };

    const getBreakBody = (item, ind) => {
      return (
        <>
          <HStack
            width={'100%'}
            p={'24px'}
            py={'21px'}
          >
            <VStack
              width={'100%'}
              space={'15px'}
            >
              {/* Session Type Field */}
              <Box width={'100%'}>
                <InputField
                  key={`sessionType_${ind}_${item.type || ''}`}
                  name='sessionType'
                  value={item.type}
                  label='Session Type'
                  placeholder='Enter Session Type'
                  width='100%'
                  onChange={(e) => onChangeSessionType(e, ind)}
                  transparent={true}
                  ind={ind}
                  speaker
                  setEventBreakError={setEventBreakError}
                  important
                  isInvalid={!!timeValidationErrors[`type_${ind}`]}
                  errorMessage={timeValidationErrors[`type_${ind}`] || ''}
                />
              </Box>
              
              <HStack
                width={'100%'}
                space={'24.5px'}
              >
                <InputField
                  name='title'
                  value={item?.title}
                  label='Title'
                  placeholder='Enter Title'
                  width='50%'
                  onChange={(e) => onSessionInputChange(e, 'title', ind)}
                  transparent={true}
                  ind={ind}
                  speaker
                  setEventBreakError={setEventBreakError}
                  important
                  isInvalid={!!timeValidationErrors[`title_${ind}`]}
                  errorMessage={timeValidationErrors[`title_${ind}`] || ''}
                />
                <InputField
                  name='location'
                  value={item.location}
                  label='Location'
                  placeholder='Enter Location'
                  width='50%'
                  onChange={(e) => onSessionInputChange(e, 'location', ind)}
                  transparent={true}
                  ind={ind}
                  speaker
                  setEventBreakError={setEventBreakError}
                  // important
                  isInvalid={!!timeValidationErrors[`location_${ind}`]}
                  errorMessage={timeValidationErrors[`location_${ind}`] || ''}
                />
              </HStack>
              <HStack
                width={'100%'}
                space={'24.5px'}
              >
                <Box
                  mt={1}
                  width='49%'
                >
                  <TimeInputField
                    name='start_time'
                    onChange={(e) => onSessionInputChange(e, 'start_time', ind)}
                    defaultValue={item.start_time}
                    label='Start Time'
                    placeholder='Select Start Time'
                    width='100%'
                    transparent={true}
                    ind={ind}
                    important
                    isInvalid={!!timeValidationErrors[`start_time_${ind}`]}
                    errorMessage={timeValidationErrors[`start_time_${ind}`] || ''}
                  />
                </Box>
                <Box
                  mt={1}
                  width='49%'
                >
                  <TimeInputField
                    name='end_time'
                    onChange={(e) => onSessionInputChange(e, 'end_time', ind)}
                    defaultValue={item.end_time}
                    label='End Time'
                    placeholder='Select End Time'
                    width='100%'
                    transparent={true}
                    ind={ind}
                    important
                    isInvalid={!!timeValidationErrors[`end_time_${ind}`]}
                    errorMessage={timeValidationErrors[`end_time_${ind}`] || ''}
                  />
                </Box>
              </HStack>
              <HStack
                width={'100%'}
                space={'24.5px'}
              >
                <Box width={'100%'}>
                  <NewTextAreaField
                    name='description'
                    value={item.description}
                    label='Description'
                    onChange={(e) =>
                      onSessionInputChange(e, 'description', ind)
                    }
                    placeholder='Enter Description'
                    transparent={true}
                    ind={ind}
                    speaker
                    setEventBreakError={setEventBreakError}
                  />
                </Box>
              </HStack>
            </VStack>
          </HStack>
        </>
      );
    };

    const getSightSeeingBody = (item, ind) => {
      return (
        <>
          <HStack
            width={'100%'}
            p={'24px'}
            py={'21px'}
          >
            <VStack
              width={'100%'}
              space={'15px'}
            >
              <HStack
                width={'100%'}
                space={'24.5px'}
              >
                <InputField
                  name='title'
                  value={item?.title}
                  label='Title'
                  placeholder='Enter Title'
                  width='50%'
                  onChange={(e) => onSessionInputChange(e, 'title', ind)}
                  transparent={true}
                  ind={ind}
                  speaker
                  setEventBreakError={setEventBreakError}
                  important
                  isInvalid={!!timeValidationErrors[`title_${ind}`]}
                  errorMessage={timeValidationErrors[`title_${ind}`] || ''}
                />
                <InputField
                  name='location'
                  value={item.location}
                  label='Location'
                  placeholder='Enter Location'
                  width='50%'
                  onChange={(e) => onSessionInputChange(e, 'location', ind)}
                  transparent={true}
                  ind={ind}
                  speaker
                  setEventBreakError={setEventBreakError}
                  // important
                  isInvalid={!!timeValidationErrors[`location_${ind}`]}
                  errorMessage={timeValidationErrors[`location_${ind}`] || ''}
                />
              </HStack>
              <HStack
                width={'100%'}
                space={'24.5px'}
              >
                <Box
                  mt={1}
                  width='49%'
                >
                  <TimeInputField
                    name='start_time'
                    onChange={(e) => onSessionInputChange(e, 'start_time', ind)}
                    defaultValue={item.start_time}
                    label='Start Time'
                    placeholder='Select Start Time'
                    width='100%'
                    transparent={true}
                    ind={ind}
                    important
                    isInvalid={!!timeValidationErrors[`start_time_${ind}`]}
                    errorMessage={timeValidationErrors[`start_time_${ind}`] || ''}
                  />
                </Box>
                <Box
                  mt={1}
                  width='49%'
                >
                  <TimeInputField
                    name='end_time'
                    onChange={(e) => onSessionInputChange(e, 'end_time', ind)}
                    defaultValue={item.end_time}
                    label='End Time'
                    placeholder='Select End Time'
                    width='100%'
                    transparent={true}
                    ind={ind}
                    important
                    isInvalid={!!timeValidationErrors[`end_time_${ind}`]}
                    errorMessage={timeValidationErrors[`end_time_${ind}`] || ''}
                  />
                </Box>
              </HStack>
              <HStack
                width={'100%'}
                space={'24.5px'}
              >
                <Box width={'48.5%'}>
                  <NewTextAreaField
                    name='description'
                    value={item.description}
                    label='Description'
                    onChange={(e) =>
                      onSessionInputChange(e, 'description', ind)
                    }
                    placeholder='Enter Description'
                    transparent={true}
                    ind={ind}
                    speaker
                    setEventBreakError={setEventBreakError}
                  />
                </Box>
              </HStack>
            </VStack>
          </HStack>
        </>
      );
    };

    const getSessionBody = (session, ind) => {
      if (!session.type || session.type.trim() === '') {
        return getBreakBody(session, ind); // Default to break form for empty types
      }
      
      switch (session.type.toLowerCase()) {
        case 'break':
          return getBreakBody(session, ind);
        case 'sightseeing':
          return getSightSeeingBody(session, ind);
        default:
          return getBreakBody(session, ind); // Default form for custom types
      }
    };

    const getSessionTitle = (session, ind) => {
      const getSessionTypeLabel = (type) => {
        if (!type || type.trim() === '') {
          return 'Session Details';
        }
        switch (type.toLowerCase()) {
          case 'break':
            return 'Session Details';
          case 'speaker':
            return 'Speaker Details';
          case 'sightseeing':
            return 'Sightseeing Details';
          default:
            return 'Session Details';
        }
      };

      return (
        <HStack
          width={'100%'}
          justifyContent={'space-between'}
          mr={4}
        >
          <Text
            fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
            color={COLORS.BLACK_COLOR}
          >
            {getSessionTypeLabel(session.type)} {ind + 1}
          </Text>
            <Pressable onPress={() => handleDeleteSession(ind)}>
              <Svg
                width={'18'}
                height={'18'}
                viewBox={`0 0 24 24`}
              >
                {deleteCardsIconSVG}
              </Svg>
            </Pressable>
          </HStack>
      );
    };

    return (
      <>
        <HStack
          alignItems={'center'}
          justifyContent={'space-between'}
          width={'100%'}
          pb={'6px'}
        >
          <Text
            fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
            fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
            color={COLORS.ATTACHMENTS_TEXT_COLOR}
          >
            Session Breakdown
          </Text>
          <CommonAvatar
            width={'46px'}
            height={'46px'}
            bgcolor={COLORS.TABS_ACTIVE_BORDER_COLOR}
            svg={addIconSVG}
            svgwh='24'
            view='0 0 24 24'
            onPress={handleAddSession}
            touchable={true}
          />
        </HStack>

        {/* Render Sessions */}
        {sessions.map((session, ind) => {
          return (
            <Box
              key={session.id}
              mb={5}
            >
              <EventPlanDetailsCards
                heading={getSessionTitle(session, ind)}
                body={getSessionBody(session, ind)}
                defaultData={session}
                width={'96%'}
              />
              
              {/* Speaker Details Section for each session */}
              <Box mt={4}>
                <HStack
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  width={'100%'}
                  pb={'6px'}
                >
                  <Text
                    fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                    fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                    color={COLORS.ATTACHMENTS_TEXT_COLOR}
                  >
                    Speaker Details
                  </Text>
                  <CommonAvatar
                    width={'46px'}
                    height={'46px'}
                    bgcolor={COLORS.TABS_ACTIVE_BORDER_COLOR}
                    svg={addIconSVG}
                    svgwh='24'
                    view='0 0 24 24'
                    onPress={() => handleAddSpeaker(ind)}
                    touchable={true}
                  />
                </HStack>
                
                {/* Render Speakers for this session */}
                {session.speakers && session.speakers.map((speaker, speakerIndex) => (
                  <Box key={speaker.id} mb={3}>
                    <EventPlanDetailsCards
                      heading={
                        <HStack
                          width={'100%'}
                          justifyContent={'space-between'}
                          mr={4}
                        >
                          <Text
                            fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                            color={COLORS.BLACK_COLOR}
                          >
                            Speaker Details {speakerIndex + 1}
                          </Text>
                          <Pressable onPress={() => handleDeleteSpeaker(ind, speakerIndex)}>
                            <Svg
                              width={'18'}
                              height={'18'}
                              viewBox={`0 0 24 24`}
                            >
                              {deleteCardsIconSVG}
                            </Svg>
                          </Pressable>
                        </HStack>
                      }
                      body={getSpeakerBody(speaker, ind, speakerIndex)}
                      defaultData={speaker}
                      width={'96%'}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}

        {/* Menu Section - Commented out Add Menu Details button */}
        {/* 
        <Box mt='24px'>
          <HStack
            alignItems={'center'}
            justifyContent={'space-between'}
            width={'100%'}
            pb={'6px'}
          >
            <Text
              fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
              fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
            >
              Add Menu Details
            </Text>
            <HStack space={2}>
              <Pressable onPress={() => setShowBulkUploadModal(true)}>
                <Text
                  fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                  color={COLORS.TABS_ACTIVE_BORDER_COLOR}
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}

                >
                  Bulk Upload
                </Text>

              </Pressable>


            </HStack>
          </HStack>
          <EventPlanTextCards
            name={'menu'}
            defaultData={defaultData}
            setDefaultData={setDefaultData}
            childref={childref}
          />
        </Box> 
        */}

        {/* Bulk Upload Menu Modal */}
        <BulkUploadMenuModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            // Refresh event details and update selected day data
            getEventDetails();
            // Force remount to refresh the component
            setForceRemount(!forceRemount);
            // Scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Close the modal after successful upload
            setShowBulkUploadModal(false);
          }}
          eventId={eventId}
          dateOfEvent={dateOfEvent}
        />
      </>
    );
  }
);

export default CreateEventBreakDown;
